/**
 * The site's public URLs, and the seam other screens plug into.
 *
 * ── WHY A REGISTRY RATHER THAN MORE `case` LABELS ───────────────────────────
 * `main.ts` owns a hand-rolled History-API switch. The landing page and the
 * footer link at `/legal`, `/legal/:slug`, `/faq` and `/news`, and those
 * screens are being built in parallel with this one. Hard-coding their imports
 * here would either couple this file to modules that do not exist yet, or pull
 * their code into the shell chunk a cold visitor downloads before first paint.
 *
 * So a screen claims its path itself, at import time:
 *
 *     registerScreen('/faq', renderFaq);            // exact
 *     registerScreen('/news/*', renderNewsArticle); // prefix
 *
 * and `main.ts` asks `resolveScreen(path)`. Until a screen registers, its path
 * renders the 404 below — which is the honest answer ("this page does not exist
 * yet") rather than the old behaviour of silently redirecting every unknown URL
 * to `/login`, which tells a visitor their bookmark logged them out.
 *
 * `/legal`, `/legal/:slug`, `/faq`, `/news` and `/news/:slug` are NOT in the
 * registry: `engine/pages/content` landed with named render functions and asked
 * routing to call them, so `main.ts` names them in its switch. The registry is
 * for the next screen, and the reason `ROUTES` is the single place those paths
 * are spelled.
 */
import { t } from '../i18n';
// SHELL-SAFE. `meta/route.ts` is a frozen table of path strings plus one lazy
// hop — no DOM, no HTTP, no screens (read its header). Importing anything else
// under `engine/meta/**` from here would move the whole meta chunk into first
// paint; importing this keeps ONE definition of which paths are meta screens.
import { ARENA_PATH, META_PATHS, isMetaPath } from '../meta/route';
import { TAP_TARGET, el, replaceChildren } from './dom';
import { skinButton } from '../ui/button';
import { createSlotIcon } from '../ui/icon-registry';

/** Canonical paths. Anything linking internally uses these, never a literal. */
export const ROUTES = Object.freeze({
  home: '/',
  /**
   * The META HOME — the Battle screen, not the match.
   *
   * It stays `/play` deliberately: the landing page's call to action points
   * here, `engine/modes/invite-link.ts` mints share links under it
   * (`INVITE_ROUTE`), and every sibling module's docblock already spells
   * `onClose: () => navigate('/play')`. The full-viewport match moved to
   * `ROUTES.arena` when the meta shell landed; renaming this instead would have
   * broken all three silently.
   */
  play: '/play',
  /** The full-viewport match surface. One definition, in `meta/route.ts`. */
  arena: ARENA_PATH,
  login: '/login',
  register: '/register',
  /**
   * WHERE THE ACCOUNT SETTINGS LIVE. Spelled from `META_PATHS`, not as a second
   * literal: the meta table is the definition, this is the shell's name for it.
   */
  settings: META_PATHS.settings,
  /**
   * A REDIRECT TARGET ONLY — `main.ts` sends `/account` to {@link ROUTES.settings}
   * and never renders a screen for it (IA-AND-NAVIGATION §4.2 step 2). The
   * constant stays because the redirect needs a name for the old path: it is in
   * bookmarks and in support macros, so it must not 404.
   */
  account: '/account',
  admin: '/admin',
  legal: '/legal',
  faq: '/faq',
  /**
   * THE SMALL GUIDE — how a match works, the cards, the factions, the ladder.
   *
   * A DOCUMENT, so it keeps the site chrome and is readable without an account:
   * it is the page the header offers a visitor who has not decided yet. It is
   * distinct from {@link ROUTES.faq}, which answers questions about the
   * SERVICE (accounts, payment, data); this one is about the GAME.
   */
  guide: '/guide',
  news: '/news',
});

/**
 * THE LAYOUT RULE, in one predicate.
 *
 * Site chrome — the header with the brand and the primary nav, and the footer
 * with the legal links, the language control and the company block — belongs to
 * every route that is a DOCUMENT. It belongs to no route that is an
 * APPLICATION SURFACE, and there are exactly two of those:
 *
 *   `/arena`  is a full-viewport canvas. A footer under it would eat the HUD
 *             and sit in the thumb zone where the hand is; a header would take
 *             a band off the top of the board. `engine/bootstrap.ts` sizes the
 *             stage to `100dvh` on the assumption that it has the viewport.
 *   the META   screens (`/play`, `/cards`, `/shop`, `/house`, `/events`,
 *   SCREENS    `/profile`, `/settings`, `/crates`, `/modes`, `/social`) draw
 *             their OWN header and their OWN bottom tab bar, sized for a thumb
 *             and pinned to the safe areas. The site footer under a sticky tab
 *             bar would push the tab bar off the fold on a 320px phone, and two
 *             navigations on one screen is the defect this predicate exists to
 *             prevent — the same reason `/admin` is excluded.
 *   `/admin*` is the operator console, which draws its OWN shell — its own
 *             brand, its own navigation, its own exit. Hanging the public
 *             marketing footer (payment brands, social links, "back to top")
 *             under it produces two navigations and two brands on one page.
 *
 * Everything else gets both: the landing page, all six auth screens, the legal
 * documents, the FAQ, the news, the PUBLIC crate drop tables, and the 404 — a
 * visitor who mistypes a URL is exactly the person who needs the nav.
 *
 * The ACCOUNT SETTINGS are not in that list any more. They are a section of the
 * `/settings` meta screen (`meta/settings-screen.ts`'s account host), and
 * `/account` is a redirect to it, so `hasSiteChrome('/account')` is now a
 * question nothing asks on a rendered route — a player changing a password
 * stays inside the game's own header and rail (IA-AND-NAVIGATION §4).
 *
 * `/crates/odds` is the interesting one: it is reached from the crate board but
 * it is a DOCUMENT, published so it can be read without an account (§4), so it
 * keeps the chrome. `isMetaPath` does not claim it, which is why this reads as
 * one predicate rather than a prefix test on `/crates`.
 *
 * Stated as a predicate rather than a `Set` because `/admin` is a whole subtree
 * and the meta screens are a table that grows.
 */
export function hasSiteChrome(path: string): boolean {
  const normalised = normalisePath(path);
  if (normalised === ROUTES.arena) return false;
  if (isMetaPath(normalised)) return false;
  return normalised !== ROUTES.admin && !normalised.startsWith(`${ROUTES.admin}/`);
}

/** A screen: render into `root`, return its teardown (or nothing). */
export type ScreenRenderer = (root: HTMLElement, path: string) => (() => void) | void;

const exact = new Map<string, ScreenRenderer>();
const prefixes: { prefix: string; render: ScreenRenderer }[] = [];

/**
 * Claim a path. `'/news/*'` claims everything under `/news/`; `'/news'` claims
 * only `/news` itself, so an index and a detail screen can be different modules.
 */
export function registerScreen(pattern: string, render: ScreenRenderer): void {
  if (pattern.endsWith('/*')) {
    const prefix = pattern.slice(0, -1); // keep the trailing slash
    prefixes.push({ prefix, render });
    // Longest prefix wins, so `/news/tag/*` can sit inside `/news/*`.
    prefixes.sort((a, b) => b.prefix.length - a.prefix.length);
    return;
  }
  exact.set(normalisePath(pattern), render);
}

/** The screen for a path, or `null` when nothing has claimed it. */
export function resolveScreen(path: string): ScreenRenderer | null {
  const normalised = normalisePath(path);
  const direct = exact.get(normalised);
  if (direct !== undefined) return direct;
  for (const { prefix, render } of prefixes) {
    if (normalised.startsWith(prefix)) return render;
  }
  return null;
}

/** `/faq/` and `/faq` are the same page; `/` stays `/`. */
export function normalisePath(path: string): string {
  if (path.length > 1 && path.endsWith('/')) return path.replace(/\/+$/, '');
  return path === '' ? '/' : path;
}

/** Test seam: forget every registration. */
export function clearScreensForTests(): void {
  exact.clear();
  prefixes.length = 0;
}

/**
 * The 404 page.
 *
 * A real page with a real heading and a way out, not a redirect. The
 * `errors.not_found_page.*` strings exist in all eight catalogues precisely so
 * a mistyped URL is answered in the visitor's language.
 */
export function renderNotFound(root: HTMLElement): () => void {
  const main = el('main', {
    id: 'main',
    class: 'mx-auto flex min-h-[60vh] w-full max-w-3xl flex-col justify-center gap-4 px-4 py-16',
  }, [
    el('h1', {
      class: 'font-heading text-2xl text-gold',
      text: t('errors.not_found_page.title'),
    }),
    el('p', { class: 'text-text-dim', text: t('errors.not_found_page.body') }),
    el('p', {}, [
      skinButton(el('a', {
        class: `${TAP_TARGET} gap-1.5 px-4`,
        href: ROUTES.home,
      }, [
        createSlotIcon(document, 'action-home', { size: 20, class: 'rga-btn__icon' }),
        el('span', { text: t('errors.not_found_page.home') }),
      ]), { variant: 'primary' }),
    ]),
  ]);

  replaceChildren(root, main);
  return () => replaceChildren(root);
}
